import React, { useCallback, useId, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import type { AccrualEntry } from '@/types';
import { Panel, PanelHeader } from '@/components/ui/Panel';
import {
  formatDecimalParts,
  formatDecimalString,
  sumDecimalStrings,
  toDecimalString,
} from '@/utils/decimal-display';
import { formatDate, formatDateShort, pluralizeUnit } from '@/utils/format';
import { isNullOrZeroDecimal } from './analytics-economics';

type ChartView = 'curve' | 'steps' | 'timeline';
type ChartRange = '1w' | '1m' | '3m' | 'all';

interface AccrualChartProps {
  accruals: AccrualEntry[] | undefined;
  isLoading: boolean;
  isError: boolean;
  currency?: string;
  /**
   * LR-749: why the rows cannot be drawn as one series (they are in two
   * currencies, see `accrualCurrency`). Shown in place of the chart.
   */
  unchartableReason?: string;
  /**
   * Forward per-day expected accrual (backend Decimal STRING, wired from
   * `trade.daily_earnings`, or from its settlement twin once the rows are in the
   * settlement currency, LR-749). Drives the Run-rate stat + the dashed forecast tail
   * (endpoint = cumulative total + forwardDailyNet), so the chart projection
   * stays in sync with KpiStrip + PnlTable Net /day.
   *
   * Three input cases (preserved from the bar-chart original — parity depends on
   * it, "the three never disagree"):
   *  - non-zero string → project total + forwardDailyNet (canonical);
   *  - zero string ("0.00000000") → LEGITIMATE flat projection: no forecast tail,
   *    Run-rate masked to "—", matching the hero's HON-04 masked "—" (NO fallback
   *    to a stale historical daily);
   *  - omitted / null → fall back to the latest historical day's net (legacy).
   */
  forwardDailyNet?: string | null;
  /**
   * Terminal state (matured / closed / cancelled). Suppresses the forecast tail
   * and turns the Run-rate stat into a "Status: Closed / accrual stopped" stat —
   * a forward forecast on a frozen position contradicts the masked KPI hero.
   */
  isTerminal?: boolean;
  /** Seed the view toggle (default 'curve'). Additive — the consumer needn't set it. */
  defaultView?: ChartView;
}

// SVG <stop> can't be targeted by a Tailwind class, so the sign-split area
// gradient needs literal color VALUES. These MIRROR the lr-pos-weak / lr-neg-weak
// tokens (tailwind.config.js → theme.extend.colors.lr) — keep them in sync. Every
// other SVG color uses `currentColor` + a `text-lr-*` class (see below).
const AREA_POS_WEAK = '#ecf6ef'; // lr-pos-weak (fill above the zero baseline)
const AREA_NEG_WEAK = '#fdeeee'; // lr-neg-weak (fill below the zero baseline)

const NARROW_MAX = 468; // container width below which the compact layout kicks in
const MIN_PLOT_WIDTH = 180; // floor so the SVG never collapses
const RANGE_MIN_DAYS = 14; // range selector only appears past this many days
const SUBCENT_MASK_DP = 2; // a daily that rounds to $0.00 at 2dp is treated as zero

/** Number() ONLY for SVG geometry ratios — never for a displayed figure (HON-03). */
function num(value: string | number | null | undefined): number {
  return Number(toDecimalString(value) ?? 0);
}

/** Next calendar day (UTC) as a bare YYYY-MM-DD, or null when unparseable. */
function nextDayIso(dateStr: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr).trim());
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1));
  return d.toISOString().slice(0, 10);
}

/** Whole calendar days from a bare YYYY-MM-DD to the current UTC day (0 = today),
 *  or null when unparseable. */
function daysAgoUtc(dateStr: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr).trim());
  if (!m) return null;
  const then = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((today - then) / 86_400_000);
}

interface SeriesPoint {
  idx: number;
  date: string;
  net: string; // grouped daily net (Decimal string)
  cum: string; // running cumulative (Decimal string)
  netNum: number; // geometry only
  cumNum: number; // geometry only
}

interface Regime {
  startIdx: number;
  endIdx: number;
  days: number;
  daily: string; // representative daily net (Decimal string)
  contribution: string; // Σ daily over the regime (Decimal string)
  startDate: string;
  endDate: string;
}

interface AccrualEvent {
  idx: number;
  type: 'open' | 'change';
  label: string;
  date: string;
}

interface Geo {
  X: (i: number) => number;
  Y: (v: number) => number;
  y0: number;
  padL: number;
  padR: number;
  padT: number;
  padB: number;
  ih: number;
  PW: number;
  H: number;
}

/**
 * Map a series window to SVG coordinates. Ported verbatim from the design
 * prototype's `buildGeo` (crisp: 1 user-unit = 1px, no viewBox scaling). The zero
 * baseline is always in-domain (anchorZero) so the sign-split fill has a real
 * split point; 16% vertical padding keeps the curve off the frame.
 */
function buildGeo(
  values: number[],
  count: number,
  H: number,
  PW: number,
  opts: {
    padT: number;
    padB: number;
    includeForecast?: boolean;
    forecastVal?: number;
    /** Default true. Sub-windows (1W/1M/3M) pass false so the domain spans the
     *  window's own min/max — anchoring zero rendered a mature book's last week
     *  as a ~2% sliver pinned at the top of the plot (LR-518 r1 #12). */
    anchorZero?: boolean;
  },
): Geo {
  const padL = 4;
  const padR = 12;
  const { padT, padB } = opts;
  const iw = PW - padL - padR;
  const ih = H - padT - padB;

  let vals = values.slice();
  if (opts.includeForecast && opts.forecastVal != null) vals = vals.concat([opts.forecastVal]);
  let dMin = Math.min(...vals);
  let dMax = Math.max(...vals);
  if (opts.anchorZero !== false) {
    dMin = Math.min(dMin, 0);
    dMax = Math.max(dMax, 0);
  }
  if (dMin === dMax) {
    dMin -= 1;
    dMax += 1;
  }
  const sp = (dMax - dMin) * 0.16;
  dMin -= sp;
  dMax += sp;

  const N = count + (opts.includeForecast ? 1 : 0);
  const X = (i: number) => padL + (N <= 1 ? iw / 2 : (i / (N - 1)) * iw);
  const Y = (v: number) => padT + ((dMax - v) / (dMax - dMin)) * ih;
  const y0 = padT + (dMax / (dMax - dMin)) * ih;

  return { X, Y, y0, padL, padR, padT, padB, ih, PW, H };
}

/**
 * A signed money figure at a chart-specific size. Mirrors `<SignedValue>` exactly
 * (both derive sign + tone from `formatDecimalParts` — one rounding source, HON-03)
 * but takes an explicit size class: the chart's dense typographic scale
 * (27/23/17/13/12px, some responsive) falls outside the three shared FigureTiers,
 * so a local sized renderer is cleaner than either an ad-hoc font size on
 * SignedValue or polluting the shared tier map. Cents are demoted (`.lr-dec`) only
 * when |value| ≥ 1, so sub-$1 figures (the −$0.86 run-rate) stay legible.
 */
function Money({
  value,
  currency,
  dp,
  unit,
  sizeClass,
  colored = true,
  className,
}: {
  value: string | number | null | undefined;
  currency?: string;
  dp?: number;
  unit?: string;
  sizeClass: string;
  colored?: boolean;
  className?: string;
}) {
  const parts = formatDecimalParts(value, { currency, dp });
  if (parts.isEmpty) {
    return <span className={clsx('lr-num text-lr-muted', sizeClass, className)}>—</span>;
  }
  const isZero =
    /^0+$/.test(parts.intGrouped.replace(/,/g, '')) && /^0*$/.test(parts.frac);
  const glyph = parts.negative ? '−' : isZero ? '' : '+';
  const tone = !colored
    ? 'text-lr-fg'
    : parts.negative
      ? 'text-lr-neg'
      : isZero
        ? 'text-lr-muted'
        : 'text-lr-pos';
  const head = `${parts.currencySymbol}${parts.intGrouped}`;
  const fracStr = parts.dp > 0 ? `.${parts.frac}` : '';
  const demote = parts.intGrouped.replace(/,/g, '') !== '0';
  return (
    <span className={clsx('lr-num', sizeClass, tone, className)}>
      {glyph && <span className="lr-sgn">{glyph}</span>}
      {head}
      {demote ? (
        (fracStr || unit) && (
          <span className="lr-dec">
            {fracStr}
            {unit}
          </span>
        )
      ) : (
        <>
          {fracStr}
          {unit && <span className="lr-dec">{unit}</span>}
        </>
      )}
    </span>
  );
}

/**
 * Screen-reader enumeration of the FULL series. Memoized on (series, eventByIdx,
 * currency) — both maps are useMemo'd upstream — so hover-driven re-renders of
 * the chart skip this N-item vDOM diff (a 3-year book is ~1,100 <li>) (LR-518
 * r1 #13).
 */
const SrSeriesList = React.memo(function SrSeriesList({
  series,
  eventByIdx,
  currency,
}: {
  series: SeriesPoint[];
  eventByIdx: Map<number, AccrualEvent>;
  currency?: string;
}) {
  return (
    <ul className="sr-only">
      {series.map((p) => {
        const ev = eventByIdx.get(p.idx);
        return (
          <li key={p.date}>
            {formatDate(p.date)}: {formatDecimalString(p.net, { currency, dp: 2 })} on the day,{' '}
            {formatDecimalString(p.cum, { currency, dp: 2 })} cumulative
            {ev ? ` — ${ev.label}` : ''}
          </li>
        );
      })}
    </ul>
  );
});

interface SegOption<T extends string> {
  k: T;
  label: string;
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: SegOption<T>[];
  value: T;
  onChange: (k: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex gap-0.5 rounded-[7px] border border-lr-border bg-lr-bg p-0.5"
    >
      {options.map((o) => {
        const active = o.k === value;
        return (
          <button
            key={o.k}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.k)}
            className={clsx(
              'rounded-[5px] px-2.5 py-[5px] text-[11.5px] font-semibold transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent/40',
              active
                ? 'bg-lr-surface text-lr-fg shadow-[0_1px_2px_rgba(16,22,32,0.12)]'
                : 'text-lr-muted hover:text-lr-fg',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Daily accrual chart — a cumulative-accrual CURVE hero + summary band + a
 * Curve / Steps / Events view toggle (redesign of the former per-day bar chart).
 * Pure inline SVG, NO chart library. Fully responsive to its container (measured
 * via ResizeObserver) so it never clips horizontally — the original bug.
 *
 * Money math + display go through the Decimal-safe helpers (HON-03); Number() is
 * used ONLY for SVG coordinate ratios. The to-date cumulative (summary hero /
 * accrual-cumulative testid) is an O(n) prefix sum and MUST equal the accrued KPI
 * hero (CR-01). Events/regimes are derived from the series (Path A): a regime is a
 * run of days with the same 2dp daily net; regime boundaries are the event
 * markers. Labeling is generic ("Rate/level change") until a rate/recall/mark feed
 * exists (Path B — additive, not wired here).
 */
export function AccrualChart({
  accruals,
  isLoading,
  isError,
  currency,
  unchartableReason,
  forwardDailyNet,
  isTerminal = false,
  defaultView = 'curve',
}: AccrualChartProps) {
  const rows = useMemo(() => accruals ?? [], [accruals]);
  // useId() contains ':' (React 18); strip it so the value is safe inside an SVG
  // element id + `url(#…)` gradient reference.
  const gradId = useId().replace(/:/g, '');

  const { days, dailyNets, cumulative, series } = useMemo(() => {
    const byDay = new Map<string, string[]>();
    for (const a of rows) {
      if (!a.accrual_date) continue;
      const list = byDay.get(a.accrual_date) ?? [];
      list.push(a.net_amount);
      byDay.set(a.accrual_date, list);
    }
    const sortedDays = [...byDay.keys()].sort();
    const nets = sortedDays.map((d) => sumDecimalStrings(byDay.get(d) ?? []));
    // O(n) running prefix sum — the last entry is the full-series total = accrued
    // hero. Fold (not re-sum each prefix) to stay O(n) on long positions.
    const cum: string[] = [];
    let running = '0';
    for (const n of nets) {
      running = sumDecimalStrings([running, n]);
      cum.push(running);
    }
    const pts: SeriesPoint[] = sortedDays.map((d, i) => ({
      idx: i,
      date: d,
      net: nets[i],
      cum: cum[i],
      netNum: num(nets[i]),
      cumNum: num(cum[i]),
    }));
    return { days: sortedDays, dailyNets: nets, cumulative: cum, series: pts };
  }, [rows]);

  const count = days.length;

  // Regimes (Path A): runs of equal 2dp daily net. The 2dp key groups by what the
  // desk actually SEES (a sub-half-cent wobble doesn't split a regime), and reuses
  // the existing formatter so there is no bespoke float epsilon.
  const regimes = useMemo<Regime[]>(() => {
    const rs: Array<Omit<Regime, 'contribution' | 'startDate' | 'endDate'> & { key: string }> = [];
    series.forEach((p, i) => {
      const key = formatDecimalString(p.net, { dp: SUBCENT_MASK_DP });
      const last = rs[rs.length - 1];
      if (last && last.key === key) {
        last.endIdx = i;
        last.days += 1;
      } else {
        rs.push({ key, startIdx: i, endIdx: i, days: 1, daily: p.net });
      }
    });
    return rs.map((r) => ({
      startIdx: r.startIdx,
      endIdx: r.endIdx,
      days: r.days,
      daily: r.daily,
      contribution: sumDecimalStrings(dailyNets.slice(r.startIdx, r.endIdx + 1)),
      startDate: days[r.startIdx],
      endDate: days[r.endIdx],
    }));
  }, [series, dailyNets, days]);

  const events = useMemo<AccrualEvent[]>(
    () =>
      regimes.map((r, k) => ({
        idx: r.startIdx,
        type: k === 0 ? 'open' : 'change',
        label: k === 0 ? 'Accrual start' : 'Rate/level change',
        date: days[r.startIdx],
      })),
    [regimes, days],
  );
  const eventByIdx = useMemo(() => {
    const m = new Map<number, AccrualEvent>();
    for (const e of events) m.set(e.idx, e);
    return m;
  }, [events]);
  const changeEvents = useMemo(() => events.filter((e) => e.type !== 'open'), [events]);
  const lastEvent = changeEvents.length ? changeEvents[changeEvents.length - 1] : null;

  // ── local UI state (no zustand) ──────────────────────────────────────────
  const [view, setView] = useState<ChartView>(defaultView);
  const [range, setRange] = useState<ChartRange>('all');
  const [hi, setHi] = useState<number | null>(null); // absolute hovered/scrubbed idx
  const [width, setWidth] = useState<number>(600);
  const [live, setLive] = useState('');

  const plotRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  // Attach the ResizeObserver via a ref CALLBACK (not a one-shot layout effect) so it
  // wires up whenever the measured node mounts — crucially on the loading→data remount,
  // where a []-dep effect fired once against the loading branch (which has no bodyRef)
  // and then never re-ran, pinning `width` at the 600 fallback → `narrow` never engaged
  // → horizontal clip on narrow containers (LR-518). The synchronous first measurement
  // also makes the first paint use the true width instead of the 600 placeholder.
  const bodyRef = useCallback((el: HTMLDivElement | null) => {
    roRef.current?.disconnect();
    roRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // Measure the CONTENT box, matching what the RO's contentRect reports —
    // getBoundingClientRect() is border-box and over-measured by the wrapper's
    // 36px padding, so every mount first painted the SVG 36px too wide and then
    // snapped narrower when the observer landed (LR-518 r1 #9).
    const cs = getComputedStyle(el);
    const w0 = Math.round(
      el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0),
    );
    if (w0 > 0) setWidth(w0);
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);

  const header = (
    <PanelHeader
      icon={<ChartBarIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden="true" />}
      title="Daily accrual"
      meta={count > 0 ? pluralizeUnit(count, 'day') : undefined}
    />
  );

  if (isLoading) {
    return (
      <Panel>
        {header}
        <div className="p-[18px]">
          <div className="h-40 animate-pulse rounded bg-gray-100" />
        </div>
      </Panel>
    );
  }
  if (isError) {
    return (
      <Panel>
        {header}
        <div className="p-[18px] text-sm text-lr-muted">Couldn&apos;t load accruals.</div>
      </Panel>
    );
  }
  if (unchartableReason) {
    return (
      <Panel>
        {header}
        <div className="p-[18px] text-sm text-lr-muted">{unchartableReason}</div>
      </Panel>
    );
  }
  if (count === 0) {
    return (
      <Panel>
        {header}
        <div className="p-[18px] text-sm text-lr-muted">
          {isTerminal ? '0 days · accrual stopped' : '0 days · no accruals yet'}
        </div>
      </Panel>
    );
  }

  // ── derived scalars ────────────────────────────────────────────────────────
  const total = cumulative[count - 1];
  const latestDaily = dailyNets[count - 1];

  // Forward forecast resolution — keep all three cases (see prop docs).
  const forwardProvided = toDecimalString(forwardDailyNet) != null;
  const forwardIsZero = forwardProvided && isNullOrZeroDecimal(forwardDailyNet);
  const resolvedDaily = forwardIsZero
    ? '0'
    : forwardProvided
      ? (toDecimalString(forwardDailyNet) as string)
      : latestDaily;
  const dailyDisplaysZero = isNullOrZeroDecimal(
    formatDecimalString(resolvedDaily, { dp: SUBCENT_MASK_DP }),
  );
  // Forecast drawn only when meaningful: not terminal, and the projected daily
  // doesn't round to $0.00 (mirrors the masked hero).
  const incF = !isTerminal && !dailyDisplaysZero;
  const fVal = incF ? sumDecimalStrings([total, resolvedDaily]) : total;
  const projIso = nextDayIso(days[count - 1]);
  const projLabel = projIso ? formatDateShort(projIso) : '';

  const narrow = width < NARROW_MAX;
  const PW = Math.max(MIN_PLOT_WIDTH, width);
  const canRange = view === 'curve' && count > RANGE_MIN_DAYS;

  // Range window = the last N accrual ROWS, not N calendar days: on a gapped
  // feed (holidays, suspended accrual) "1W" can span more than a week — the
  // axis row prints the window's real first/mid/last dates, so the true span
  // is always visible (accepted, LR-518 r2 #9). startIdx offsets absolute↔window.
  const sliceDays =
    range === '1w' ? 7 : range === '1m' ? 30 : range === '3m' ? 90 : count;
  const startIdx = canRange && range !== 'all' ? Math.max(0, count - sliceDays) : 0;
  const win = series.slice(startIdx);

  const hoveredPt =
    hi != null && hi >= startIdx && hi < startIdx + win.length ? win[hi - startIdx] : null;

  const announce = (p: SeriesPoint): string => {
    const ev = eventByIdx.get(p.idx);
    const cum = formatDecimalString(p.cum, { currency, dp: 2 });
    const net = formatDecimalString(p.net, { currency, dp: 2 });
    return `${formatDate(p.date)}, accrued ${cum}, on the day ${net}${ev ? `, ${ev.label}` : ''}`;
  };

  const clearHover = () => {
    setHi(null);
    setLive('');
  };

  // Nearest index from a client X (plotRef spans exactly PW px → 1:1 to X()).
  // O(1) inverse of X(): points are evenly spaced, so the nearest data index is
  // the rounded affine inversion — the previous linear scan ran O(n) per
  // mousemove on multi-year books (LR-518 r1 #13). `len` = data points; `nSlots`
  // = plotted slots (len + 1 when the forecast tail occupies the last X slot).
  const nearestIdx = (clientX: number, geo: Geo, len: number, nSlots: number): number => {
    const el = plotRef.current;
    if (!el) return len - 1;
    const r = el.getBoundingClientRect();
    const px = clientX - r.left;
    const iw = geo.PW - geo.padL - geo.padR;
    if (nSlots <= 1 || iw <= 0) return 0;
    const j = Math.round(((px - geo.padL) * (nSlots - 1)) / iw);
    return Math.max(0, Math.min(len - 1, j));
  };

  const scrubKey = (
    e: React.KeyboardEvent,
    len: number,
    toAbsolute: (j: number) => number,
    at: (j: number) => SeriesPoint,
    current: number,
  ) => {
    let j = current;
    if (e.key === 'ArrowLeft') j -= 1;
    else if (e.key === 'ArrowRight') j += 1;
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = len - 1;
    else return;
    e.preventDefault();
    j = Math.max(0, Math.min(len - 1, j));
    setHi(toAbsolute(j));
    setLive(announce(at(j)));
  };

  const money = (
    value: string | number | null | undefined,
    sizeClass: string,
    dp?: number,
    unit?: string,
  ) => <Money value={value} currency={currency} dp={dp} unit={unit} sizeClass={sizeClass} />;

  // ── summary band ───────────────────────────────────────────────────────────
  // Typography aligned to the sibling trade-detail cards (AdequacyCard / RateWaterfall):
  // Inter (NOT font-mono — mono is reserved for ISIN/account values), semibold (600)
  // figures, 11px/0.5px-tracking caps labels, 11.5px faint sub-lines. `lr-num` gives
  // tabular figures without switching to a monospace face.
  const heroSize = narrow ? 'text-[19px] font-semibold tracking-[-0.4px]' : 'text-[22px] font-semibold tracking-[-0.4px]';
  const capsLabel = 'text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted';
  const subLine = 'mt-1 truncate text-[11.5px] text-lr-faint';

  const cumStat = (
    <div className="min-w-0 flex-[1_1_auto]">
      <div className={clsx(capsLabel, 'mb-1.5 whitespace-nowrap')}>
        {hoveredPt ? 'Accrued as of date' : 'Accrued to date'}
      </div>
      <span data-testid="accrual-cumulative">
        {money(hoveredPt ? hoveredPt.cum : total, heroSize)}
      </span>
      <div className={subLine}>
        {hoveredPt
          ? formatDate(hoveredPt.date)
          : `${pluralizeUnit(count, 'day')} · from ${formatDate(days[0])}`}
      </div>
    </div>
  );

  const rrStat = isTerminal ? (
    <div className="min-w-0 flex-[1_1_auto]">
      <div className={clsx(capsLabel, 'mb-1.5 whitespace-nowrap')}>Status</div>
      <div className="text-[15px] font-semibold leading-none text-lr-muted">Closed</div>
      <div className={subLine}>accrual stopped</div>
    </div>
  ) : (
    <div className="min-w-0 flex-[1_1_auto]">
      <div className={clsx(capsLabel, 'mb-1.5 whitespace-nowrap')}>Run-rate</div>
      {dailyDisplaysZero ? (
        <div className="text-[17px] font-semibold leading-none text-lr-muted">—</div>
      ) : (
        <div className="flex items-baseline gap-1">
          {money(resolvedDaily, 'text-[17px] font-semibold leading-none')}
          <span className="text-[10.5px] font-semibold text-lr-faint">/day</span>
        </div>
      )}
      <div className={subLine}>{incF ? `projected to ${projLabel}` : 'at current rate'}</div>
    </div>
  );

  // LR-518 r1 #4: "today / N days ago" is CALENDAR distance from the event's own
  // date — row-index distance counts accrual ROWS, so a matured trade whose final
  // day changed rate read "today" forever, and a lagging feed understated the age.
  // A terminal (frozen) series can never truthfully say "today": show the
  // absolute event date instead. Same for a FUTURE-dated event (pre-booked
  // accrual / client clock behind the business date): negative distance falls
  // back to the absolute date, never a false "today" (r2 #7).
  const lastEventAgo = lastEvent != null ? daysAgoUtc(lastEvent.date) : null;
  const evStat =
    lastEvent != null ? (
      <div className="min-w-0 flex-[1_1_auto]">
        <div className={clsx(capsLabel, 'mb-1.5 whitespace-nowrap')}>Last event</div>
        <div className="lr-num text-[15px] font-semibold leading-none text-lr-fg">
          {isTerminal || lastEventAgo == null || lastEventAgo < 0
            ? formatDate(lastEvent.date)
            : lastEventAgo === 0
              ? 'today'
              : `${pluralizeUnit(lastEventAgo, 'day')} ago`}
        </div>
        <div className={subLine}>{lastEvent.label}</div>
      </div>
    ) : null;

  const divider = <div className="w-px self-stretch bg-lr-border" aria-hidden="true" />;

  const summaryBand = (
    <div
      className={clsx('flex flex-wrap items-start', narrow ? 'gap-3.5' : 'gap-[18px]')}
    >
      {cumStat}
      {!narrow && divider}
      {rrStat}
      {evStat && (
        <>
          {!narrow && divider}
          {narrow && <div className="h-0 basis-full" aria-hidden="true" />}
          {evStat}
        </>
      )}
    </div>
  );

  // ── shared axis + footer ───────────────────────────────────────────────────
  const axisFmt = (dateStr: string, withYear: boolean) =>
    withYear ? formatDate(dateStr) : formatDateShort(dateStr);

  // Steps-tooltip date line: the regime's calendar span, not just "Duration N
  // days" (QA follow-up — the Curve tooltip carries a date, Steps must too).
  const regimeSpan = (r: Regime): string => {
    if (r.startDate === r.endDate) return formatDate(r.startDate);
    // Both ends in full ISO: a `MM-DD – YYYY-MM-DD` mix reads as two different formats.
    return `${formatDate(r.startDate)} – ${formatDate(r.endDate)}`;
  };

  const axisRow = (arr: SeriesPoint[], projTail: boolean) => (
    <div className="mt-2 flex justify-between text-[11px] font-medium text-lr-faint lr-num">
      <span>{axisFmt(arr[0].date, arr.length > 60)}</span>
      {arr.length > 3 && <span>{axisFmt(arr[Math.floor(arr.length / 2)].date, false)}</span>}
      <span>
        {projTail ? `${projLabel} ›` : axisFmt(arr[arr.length - 1].date, false)}
      </span>
    </div>
  );

  // Forecast affordances only where a forecast element is actually DRAWN — the
  // curve's dashed tail. The steps plot has none (its dashed verticals are regime
  // flags), so a dashed "forecast" legend or a "$X/day → date" readout under it
  // described a different chart (LR-518 r1 #11).
  const showForecastFooter = incF && view === 'curve';
  // Same honesty for the event chip (r2 #3): the curve draws a marker per
  // in-window event; the steps plot only flags regime CHANGES — a constant-rate
  // book draws none, so its legend must not advertise one. When nothing remains
  // on either side, drop the footer row entirely.
  const showEventChip =
    view === 'curve' ? events.some((e) => e.idx >= startIdx) : changeEvents.length > 0;
  const footer =
    view === 'timeline' || (!showEventChip && !showForecastFooter && !isTerminal) ? null : (
      <div className="mt-3 flex flex-wrap justify-between gap-2 border-t border-lr-border pt-3 text-[12px] text-lr-muted">
        <span className="flex items-center gap-3">
          {showEventChip && (
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block h-[9px] w-[9px] rounded-full border-[1.6px] border-lr-accent bg-white"
                aria-hidden="true"
              />
              event
            </span>
          )}
          {showForecastFooter && (
            <span className="flex items-center gap-1.5">
              <span
                className="inline-block w-3.5 border-t-[1.6px] border-dashed border-lr-fg-2 opacity-60"
                aria-hidden="true"
              />
              forecast
            </span>
          )}
        </span>
        <span className="lr-num">
          {isTerminal
            ? 'accrual stopped'
            : showForecastFooter
              ? `${formatDecimalString(resolvedDaily, { currency })}/day → ${projLabel}`
              : ''}
        </span>
      </div>
    );

  // ── tooltip ────────────────────────────────────────────────────────────────
  const tooltip = (p: SeriesPoint, cx: number, cy: number) => {
    const ev = eventByIdx.get(p.idx);
    const flip = cx > PW * 0.55;
    return (
      <div
        className="pointer-events-none absolute z-[6] min-w-[138px] whitespace-nowrap rounded-[8px] border border-lr-border-2 bg-white px-[11px] py-[9px] shadow-[0_8px_22px_rgba(16,22,32,0.14)]"
        style={{
          top: `${Math.max(2, cy - 12)}px`,
          left: `${cx}px`,
          transform: flip
            ? 'translate(-100%, -50%) translateX(-13px)'
            : 'translate(0, -50%) translateX(13px)',
        }}
      >
        <div className="mb-[5px] lr-num text-[11px] font-semibold text-lr-faint">
          {formatDate(p.date)}
        </div>
        <div className="mt-[3px] flex items-baseline justify-between gap-4">
          <span className="text-[11.5px] text-lr-muted">Accrued</span>
          {money(p.cum, 'text-[13px] font-semibold')}
        </div>
        <div className="mt-[3px] flex items-baseline justify-between gap-4">
          <span className="text-[11.5px] text-lr-muted">On the day</span>
          {money(p.net, 'text-[12px] font-semibold')}
        </div>
        {ev && (
          <div className="mt-[7px] whitespace-normal border-t border-lr-border pt-[7px] text-[11px] font-semibold text-lr-accent">
            ● {ev.label}
          </div>
        )}
      </div>
    );
  };

  // ── Curve view ─────────────────────────────────────────────────────────────
  const renderCurve = () => {
    const H = narrow ? 150 : 176;
    const geo = buildGeo(
      win.map((p) => p.cumNum),
      win.length,
      H,
      PW,
      {
        padT: 14,
        padB: 6,
        includeForecast: incF,
        forecastVal: num(fVal),
        // Full-series windows anchor zero (the sign-split fill needs the real
        // baseline); 1W/1M/3M sub-windows span their own min/max (r1 #12).
        anchorZero: startIdx === 0,
      },
    );
    const { X, Y, y0, padL, padR, padT, padB, ih } = geo;
    const lastJ = win.length - 1;
    // With an unanchored sub-window the zero line usually leaves the domain:
    // clamp the area/stem base into the plot and draw the baseline only when
    // zero is actually visible.
    const base = Math.min(Math.max(y0, padT), H - padB);
    const zeroVisible = y0 >= padT && y0 <= H - padB;

    const linePath =
      win.length > 1
        ? win.map((p, j) => `${j === 0 ? 'M' : 'L'}${X(j).toFixed(1)},${Y(p.cumNum).toFixed(1)}`).join(' ')
        : `M${X(0).toFixed(1)},${Y(win[0].cumNum).toFixed(1)}`;
    const areaPath = `${linePath} L${X(lastJ).toFixed(1)},${base.toFixed(1)} L${X(0).toFixed(1)},${base.toFixed(1)} Z`;
    const zf = Math.max(0, Math.min(1, (y0 - padT) / ih));

    const hoverJ = hoveredPt ? hoveredPt.idx - startIdx : -1;

    return (
      <>
        <div
          ref={plotRef}
          className="relative rounded-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent/40"
          tabIndex={0}
          role="img"
          aria-label={`Cumulative accrual, ${pluralizeUnit(count, 'day')}, ${formatDecimalString(total, { currency, dp: 2 })} to date`}
          onKeyDown={(e) =>
            scrubKey(
              e,
              win.length,
              (j) => startIdx + j,
              (j) => win[j],
              hoveredPt ? hoveredPt.idx - startIdx : win.length - 1,
            )
          }
        >
          <svg width={PW} height={H} className="block">
            <defs>
              <linearGradient
                id={`${gradId}-area`}
                x1="0"
                y1={padT}
                x2="0"
                y2={padT + ih}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor={AREA_POS_WEAK} />
                <stop offset={zf} stopColor={AREA_POS_WEAK} />
                <stop offset={zf} stopColor={AREA_NEG_WEAK} />
                <stop offset="1" stopColor={AREA_NEG_WEAK} />
              </linearGradient>
            </defs>

            <path d={areaPath} fill={`url(#${gradId}-area)`} opacity={0.92} />

            {zeroVisible && (
              <line
                data-testid="accrual-zero-baseline"
                x1={padL}
                y1={y0}
                x2={PW - padR}
                y2={y0}
                className="text-lr-border-2"
                stroke="currentColor"
                strokeWidth={1}
              />
            )}

            {/* forecast tail */}
            {incF && (
              <>
                <path
                  d={`M${X(lastJ)},${Y(win[lastJ].cumNum)} L${X(win.length)},${Y(num(fVal))}`}
                  className="text-lr-fg-2"
                  stroke="currentColor"
                  strokeWidth={1.6}
                  strokeDasharray="4 3"
                  fill="none"
                  opacity={0.6}
                />
                <circle
                  cx={X(win.length)}
                  cy={Y(num(fVal))}
                  r={3.4}
                  fill="#ffffff"
                  className="text-lr-fg-2"
                  stroke="currentColor"
                  strokeWidth={1.4}
                  opacity={0.75}
                />
              </>
            )}

            {/* event markers */}
            {events.map((e) => {
              if (e.idx < startIdx || e.idx >= startIdx + win.length) return null;
              const j = e.idx - startIdx;
              const active = hoveredPt?.idx === e.idx;
              return (
                <g key={e.idx}>
                  <line
                    x1={X(j)}
                    y1={Y(win[j].cumNum)}
                    x2={X(j)}
                    y2={base}
                    className="text-lr-accent-line"
                    stroke="currentColor"
                    strokeWidth={1}
                    opacity={0.85}
                  />
                  <circle
                    cx={X(j)}
                    cy={Y(win[j].cumNum)}
                    r={active ? 5 : 4}
                    fill="#ffffff"
                    className="text-lr-accent"
                    stroke="currentColor"
                    strokeWidth={1.6}
                  />
                </g>
              );
            })}

            {win.length > 1 && (
              <path
                d={linePath}
                fill="none"
                className="text-lr-fg-2"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            )}

            {/* today dot */}
            <circle
              cx={X(lastJ)}
              cy={Y(win[lastJ].cumNum)}
              r={3.2}
              className="text-lr-accent"
              fill="currentColor"
            />

            {/* hover crosshair + dot */}
            {hoveredPt && (
              <>
                {/* Full plot height (not `base`): on a net-negative book/window the
                    zero line sits at/above padT, and a base-bound guide collapsed to
                    a ~2px stub (r2 #2). Matches the Steps view's crosshair. */}
                <line
                  x1={X(hoverJ)}
                  y1={padT - 2}
                  x2={X(hoverJ)}
                  y2={H - padB}
                  className="text-lr-fg-2"
                  stroke="currentColor"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  opacity={0.5}
                />
                <circle
                  cx={X(hoverJ)}
                  cy={Y(hoveredPt.cumNum)}
                  r={4.5}
                  className="text-lr-accent"
                  fill="currentColor"
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              </>
            )}

            {/* transparent capture */}
            <rect
              x={0}
              y={0}
              width={PW}
              height={H}
              fill="transparent"
              style={{ cursor: 'crosshair' }}
              onMouseMove={(e) => {
                const j = nearestIdx(e.clientX, geo, win.length, win.length + (incF ? 1 : 0));
                setHi(startIdx + j);
                setLive(announce(win[j]));
              }}
              onMouseLeave={clearHover}
            />
          </svg>

          {hoveredPt && tooltip(hoveredPt, X(hoverJ), Y(hoveredPt.cumNum))}
        </div>
        {axisRow(win, incF)}
      </>
    );
  };

  // ── Steps view ─────────────────────────────────────────────────────────────
  const renderSteps = () => {
    const H = narrow ? 138 : 158;
    const geo = buildGeo(
      series.map((p) => p.netNum),
      count,
      H,
      PW,
      { padT: 18, padB: 14 },
    );
    const { X, Y, y0, padL, padR, padT, padB } = geo;

    let stepPath = '';
    series.forEach((p, j) => {
      const x = X(j);
      const y = Y(p.netNum);
      if (j === 0) stepPath += `M${x.toFixed(1)},${y.toFixed(1)}`;
      else stepPath += ` L${x.toFixed(1)},${Y(series[j - 1].netNum).toFixed(1)} L${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const areaPath = `${stepPath} L${X(count - 1).toFixed(1)},${y0.toFixed(1)} L${X(0).toFixed(1)},${y0.toFixed(1)} Z`;

    const hoverJ = hoveredPt ? hoveredPt.idx : -1;
    const hoverRegime = hoveredPt
      ? regimes.find((r) => hoveredPt.idx >= r.startIdx && hoveredPt.idx <= r.endIdx)
      : null;

    return (
      <>
        <div
          ref={plotRef}
          className="relative rounded-[6px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent/40"
          tabIndex={0}
          role="img"
          aria-label={`Daily rate steps, ${pluralizeUnit(regimes.length, 'regime')}`}
          onKeyDown={(e) =>
            scrubKey(
              e,
              count,
              (j) => j,
              (j) => series[j],
              hoveredPt ? hoveredPt.idx : count - 1,
            )
          }
        >
          <svg width={PW} height={H} className="block">
            <path d={areaPath} className="text-lr-accent-weak" fill="currentColor" opacity={0.6} />
            <line
              x1={padL}
              y1={y0}
              x2={PW - padR}
              y2={y0}
              className="text-lr-border-2"
              stroke="currentColor"
              strokeWidth={1}
            />

            {/* regime-change flags */}
            {changeEvents.map((e) => (
              <g key={e.idx}>
                <line
                  x1={X(e.idx)}
                  y1={padT - 2}
                  x2={X(e.idx)}
                  y2={H - padB}
                  className="text-lr-accent-line"
                  stroke="currentColor"
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
                <circle
                  cx={X(e.idx)}
                  cy={Y(series[e.idx].netNum)}
                  r={3.6}
                  fill="#ffffff"
                  className="text-lr-accent"
                  stroke="currentColor"
                  strokeWidth={1.6}
                />
              </g>
            ))}

            <path
              d={stepPath}
              fill="none"
              className="text-lr-fg-2"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinejoin="round"
            />

            {count === 1 && (
              // A one-day series yields a bare "M" path that draws nothing —
              // mark the lone regime's level explicitly, mirroring the curve
              // view's today-dot (LR-518 r1 #17).
              <circle
                cx={X(0)}
                cy={Y(series[0].netNum)}
                r={3.2}
                className="text-lr-accent"
                fill="currentColor"
              />
            )}

            {hoveredPt && (
              <>
                <line
                  x1={X(hoverJ)}
                  y1={padT - 2}
                  x2={X(hoverJ)}
                  y2={H - padB}
                  className="text-lr-fg-2"
                  stroke="currentColor"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                  opacity={0.5}
                />
                <circle
                  cx={X(hoverJ)}
                  cy={Y(hoveredPt.netNum)}
                  r={4}
                  className="text-lr-accent"
                  fill="currentColor"
                  stroke="#ffffff"
                  strokeWidth={2}
                />
              </>
            )}

            <rect
              x={0}
              y={0}
              width={PW}
              height={H}
              fill="transparent"
              style={{ cursor: 'crosshair' }}
              onMouseMove={(e) => {
                const j = nearestIdx(e.clientX, geo, count, count);
                setHi(j);
                setLive(announce(series[j]));
              }}
              onMouseLeave={clearHover}
            />
          </svg>

          {hoveredPt && hoverRegime && (
            <div
              className="pointer-events-none absolute z-[6] min-w-[150px] whitespace-nowrap rounded-[8px] border border-lr-border-2 bg-white px-[11px] py-[9px] shadow-[0_8px_22px_rgba(16,22,32,0.14)]"
              style={{
                top: `${Math.max(2, Y(hoveredPt.netNum) - 12)}px`,
                left: `${X(hoverJ)}px`,
                transform:
                  X(hoverJ) > PW * 0.55
                    ? 'translate(-100%, -50%) translateX(-13px)'
                    : 'translate(0, -50%) translateX(13px)',
              }}
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted">
                Regime
              </div>
              <div className="mb-[5px] mt-[2px] lr-num text-[11px] font-semibold text-lr-faint">
                {regimeSpan(hoverRegime)}
              </div>
              <div className="mt-[3px] flex items-baseline justify-between gap-4">
                <span className="text-[11.5px] text-lr-muted">Per day</span>
                {money(hoverRegime.daily, 'text-[12px] font-semibold')}
              </div>
              <div className="mt-[3px] flex items-baseline justify-between gap-4">
                <span className="text-[11.5px] text-lr-muted">Duration</span>
                <span className="lr-num text-[11.5px] font-semibold text-lr-fg-2">
                  {pluralizeUnit(hoverRegime.days, 'day')}
                </span>
              </div>
              <div className="mt-[3px] flex items-baseline justify-between gap-4 border-t border-lr-border pt-[5px]">
                <span className="text-[11.5px] text-lr-muted">Contribution</span>
                {money(hoverRegime.contribution, 'text-[12px] font-semibold')}
              </div>
            </div>
          )}
        </div>

        {/* regime legend */}
        <div className="mt-[11px] flex flex-wrap gap-x-3 gap-y-1 text-[11.5px] text-lr-muted">
          {regimes.map((r) => {
            // LR-518 r1 #10: chip tone follows the same 2dp rounding as the
            // printed figure — a raw-sign check painted a red chip next to
            // "$0.00/d" for a sub-cent negative (parts.negative is already false
            // once the value rounds to zero), and green next to an exact-zero
            // regime. Zero gets a neutral chip.
            const parts = formatDecimalParts(r.daily, { dp: SUBCENT_MASK_DP });
            const chipZero =
              /^0+$/.test(parts.intGrouped.replace(/,/g, '')) && /^0*$/.test(parts.frac);
            return (
              <span key={r.startIdx} className="flex items-center gap-1.5">
                <span
                  className={clsx(
                    'inline-block h-[7px] w-[7px] rounded-[2px] opacity-70',
                    parts.negative ? 'bg-lr-neg' : chipZero ? 'bg-lr-border-2' : 'bg-lr-pos',
                  )}
                  aria-hidden="true"
                />
                <span className="lr-num">
                  {formatDecimalString(r.daily, { currency })}/d · {pluralizeUnit(r.days, 'day')}
                </span>
              </span>
            );
          })}
        </div>
      </>
    );
  };

  // ── Events (timeline) view ─────────────────────────────────────────────────
  const renderTimeline = () => {
    const rows = events.map((e, k) => {
      const next = events[k + 1];
      const endIdx = next ? next.idx - 1 : count - 1;
      return {
        e,
        daily: series[e.idx].net,
        days: endIdx - e.idx + 1,
        cumAt: cumulative[endIdx],
      };
    });

    return (
      <div role="list" aria-label="Trade events" className="flex flex-col">
        {rows.map((r, k) => {
          const isLastReal = k === rows.length - 1 && !incF;
          const active =
            hoveredPt != null &&
            hoveredPt.idx >= r.e.idx &&
            (rows[k + 1] ? hoveredPt.idx < rows[k + 1].e.idx : true);
          return (
            <div
              key={r.e.idx}
              role="listitem"
              // Keyboard parity with the Curve/Steps scrub (r2 #8): focus drives
              // the same highlight + aria-live announce that hover does.
              tabIndex={0}
              className={clsx(
                'flex gap-3 rounded-[6px] px-2 py-2 transition-colors motion-reduce:transition-none',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lr-accent/40',
                active && 'bg-lr-accent-weak',
              )}
              onMouseEnter={() => {
                setHi(r.e.idx);
                setLive(announce(series[r.e.idx]));
              }}
              onMouseLeave={clearHover}
              onFocus={() => {
                setHi(r.e.idx);
                setLive(announce(series[r.e.idx]));
              }}
              onBlur={clearHover}
            >
              <div className="flex flex-col items-center">
                <span
                  className={clsx(
                    'h-[13px] w-[13px] rounded-full border-2 border-lr-accent',
                    r.e.type === 'open' ? 'bg-lr-accent' : 'bg-white',
                  )}
                  aria-hidden="true"
                />
                {!isLastReal && (
                  <span className="mt-[3px] min-h-[16px] w-0.5 flex-1 bg-lr-border" aria-hidden="true" />
                )}
              </div>
              <div className="min-w-0 flex-1 pb-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-semibold text-lr-fg">{r.e.label}</span>
                  <span className="lr-num text-[11px] font-semibold leading-none text-lr-faint">
                    {formatDate(r.e.date)}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3.5 text-[11.5px] text-lr-muted">
                  <span className="lr-num">
                    {formatDecimalString(r.daily, { currency })}/day · {pluralizeUnit(r.days, 'day')}
                  </span>
                  <span>
                    cumulative {money(r.cumAt, 'text-[12px] font-semibold')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* projected node */}
        {incF && (
          <div role="listitem" className="flex gap-3 px-2 py-2">
            <div className="flex flex-col items-center">
              <span
                className="h-[13px] w-[13px] rounded-full border-2 border-dashed border-lr-fg-2 bg-white opacity-70"
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-semibold text-lr-muted">Forecast (run-rate)</span>
                <span className="lr-num text-[11px] font-semibold leading-none text-lr-faint">
                  {projLabel}
                </span>
              </div>
              <div className="mt-1 text-[11.5px] text-lr-muted">
                <span className="lr-num">{formatDecimalString(resolvedDaily, { currency })}/day</span>{' '}
                · continues at current rate
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Panel>
      {header}
      <div ref={bodyRef} className="px-[18px] pb-[17px] pt-4">
        {summaryBand}

        <div className="my-3 flex flex-wrap items-center justify-between gap-2.5">
          <Segmented<ChartView>
            ariaLabel="Chart view"
            value={view}
            onChange={(k) => {
              setView(k);
              clearHover();
            }}
            options={[
              { k: 'curve', label: 'Curve' },
              { k: 'steps', label: 'Steps' },
              { k: 'timeline', label: 'Events' },
            ]}
          />
          {canRange ? (
            <Segmented<ChartRange>
              ariaLabel="Range"
              value={range}
              onChange={(k) => {
                setRange(k);
                clearHover();
              }}
              options={[
                { k: '1w', label: '1W' },
                { k: '1m', label: '1M' },
                { k: '3m', label: '3M' },
                { k: 'all', label: 'All' },
              ]}
            />
          ) : (
            <span />
          )}
        </div>

        {view === 'curve' && renderCurve()}
        {view === 'steps' && renderSteps()}
        {view === 'timeline' && renderTimeline()}

        {footer}

        {/* Screen-reader enumeration of the FULL series (the plots are role=img
            with only a summary label; this is the per-day non-visual affordance). */}
        <SrSeriesList series={series} eventByIdx={eventByIdx} currency={currency} />
        <div className="sr-only" aria-live="polite">
          {live}
        </div>
      </div>
    </Panel>
  );
}
